/**
 * What `make doctor` actually prints.
 *
 * The four groups and every probe name under them are read off
 * `internal/health/doctor.go` — `timed("connect+ping")`, `timed("transactions")`,
 * `timed("lease acquire/renew/release (Lua CAS)")` and the rest. They are not a
 * plausible-looking list; a reader who runs the command gets these lines.
 *
 * Latencies are the one invented part, and they are invented conservatively:
 * they are what a local dependency on the same host looks like, not a number
 * chosen to impress. The section says so.
 *
 * `transactions` reports **warn**, and that is deliberate rather than an
 * oversight dressed up as one. MongoDB here is standalone, so there are no
 * transactions; the doctor says so out loud instead of reporting a green tick
 * for a capability the deployment does not have. Putting a warning on a
 * marketing page is the point of the section it appears in.
 */

export type ProbeStatus = 'ok' | 'warn' | 'fail'

export interface Probe {
  name: string
  status: ProbeStatus
  ms: number
}

export interface DoctorGroup {
  name: string
  target: string
  status: ProbeStatus
  probes: readonly Probe[]
}

export const DOCTOR: readonly DoctorGroup[] = [
  {
    name: 'mongodb',
    target: 'mongodb://127.0.0.1:27017/kimene',
    status: 'warn',
    probes: [
      { name: 'connect+ping', status: 'ok', ms: 4 },
      { name: 'server info', status: 'ok', ms: 2 },
      { name: 'transactions', status: 'warn', ms: 1 },
      { name: 'timeseries collections', status: 'ok', ms: 6 },
      { name: 'write/read/delete probe', status: 'ok', ms: 9 },
      { name: 'index creation', status: 'ok', ms: 12 },
    ],
  },
  {
    name: 'rabbitmq',
    target: 'amqp://127.0.0.1:5672/',
    status: 'ok',
    probes: [
      { name: 'connect', status: 'ok', ms: 7 },
      { name: 'publisher confirms', status: 'ok', ms: 11 },
      { name: 'quorum queues', status: 'ok', ms: 24 },
      { name: 'stream queues', status: 'ok', ms: 18 },
      { name: 'publish/consume/ack roundtrip', status: 'ok', ms: 15 },
    ],
  },
  {
    name: 'redis',
    target: 'redis://127.0.0.1:6379/0',
    status: 'ok',
    probes: [
      { name: 'connect+ping', status: 'ok', ms: 1 },
      { name: 'set/get/delete with TTL', status: 'ok', ms: 2 },
      { name: 'lease acquire/renew/release (Lua CAS)', status: 'ok', ms: 3 },
    ],
  },
  {
    name: 'mqtt',
    target: 'mqtts://127.0.0.1:8883',
    status: 'ok',
    probes: [
      { name: 'connect', status: 'ok', ms: 8 },
      { name: 'subscribe/publish QoS 1 roundtrip', status: 'ok', ms: 13 },
      { name: 'retained message set and clear', status: 'ok', ms: 10 },
    ],
  },
] as const

/** cmd/ — the four binaries `make build` produces. */
export const BINARIES = ['kimene-server', 'kimene-worker', 'kimene-node', 'kimene'] as const
